import React, { useMemo, useState } from 'react';
import trainerRowStyles from './TrainerRow.module.scss';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import Person4OutlinedIcon from '@mui/icons-material/Person4Outlined';
import EmptyState from '../composites/EmptyState';
import ContentDrill from '../composites/ContentDrill';
import TrainerRow from './TrainerRow';
import ListContent from '../composites/ListContent';
import { getFullName } from '../../utils/dashboardUtils';
import AddTrainerDialog from './AddTrainerDialog';
import { useSessionData, useUserAction } from '../../providers';

import type {
  AppState,
  TrainerDashboardData,
} from '../../appState/appInitialState';
import type { Trainer, Manager, WithOptionalId } from '../../types';

const trainerListTitles = ['Trainer Name', 'Clients', 'Options'];

export interface TrainersContentStateProps {
  manager?: Manager;
  allGymTrainers: TrainerDashboardData[];
  gymId: AppState['selectedGymId'];
}

export interface TrainersContentProps extends TrainersContentStateProps {}

const Trainers: React.FC = () => {
  const { createTrainer, updateTrainer } = useUserAction();
  const { loginManager, gym, trainers: allGymTrainers } = useSessionData();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedTrainer, setSelectedTrainer] = useState<string>('');
  const [isAddTrainerModalOpened, setIsAddTrainerModalOpened] = useState(false);

  const filteredTrainers = useMemo(() => {
    if (!Object.values(allGymTrainers).length) return [];
    const searchQueryRegex = new RegExp(searchQuery.toLowerCase());
    return Object.values(allGymTrainers)
      .map(({ trainer }) => trainer)
      .filter((trainer) =>
        searchQueryRegex.test(getFullName(trainer).toLowerCase()),
      )
      .sort(
        (a, b) =>
          a.firstName
            ?.toLowerCase()
            .localeCompare(b.firstName?.toLowerCase() || '') || 0,
      );
  }, [searchQuery, allGymTrainers]);

  if (!loginManager || !gym) {
    return (
      <EmptyState icon={<Person4OutlinedIcon />} text="No trainers found." />
    );
  }

  const renderEmptyState = () => (
    <EmptyState
      icon={<Person4OutlinedIcon />}
      cta={{
        icon: <Person4OutlinedIcon color="primary" />,
        label: 'Add A Trainer',
        onClick: () => setIsAddTrainerModalOpened(true),
      }}
      text="No Trainers Have Been Added"
    />
  );

  const addTrainer = async (
    trainer: WithOptionalId<Trainer>,
  ): Promise<boolean> => {
    const success = await createTrainer(trainer, gym?.id || '');
    if (!success) {
      return false;
    }
    setIsAddTrainerModalOpened(false);
    return true;
  };

  const renderAddProgramDialog = () => (
    <AddTrainerDialog
      onSubmit={addTrainer}
      onClose={() => setIsAddTrainerModalOpened(false)}
      isOpen={isAddTrainerModalOpened}
    />
  );

  const deleteTrainer = async (trainer: Trainer) => {
    await updateTrainer({ ...trainer, isActive: false });
  };

  const renderContent = () => {
    return (
      <ListContent
        items={filteredTrainers}
        listHeaderClassName={trainerRowStyles.listGrid}
        EmptyStateComponent={renderEmptyState}
        titles={trainerListTitles}
        ItemComponent={({ data }) => (
          <TrainerRow
            trainer={data}
            deleteTrainer={deleteTrainer}
            selected={selectedTrainer === data.id}
            onSelect={() => setSelectedTrainer(data.id)}
            key={data.id}
          />
        )}
      />
    );
  };

  const getSearchPlaceholder = () => {
    if (!filteredTrainers.length) return '';
    return 'Search for Trainers';
  };

  return (
    <ContentDrill
      title="Trainers"
      isParent
      actions={[
        {
          icon: <AddBoxOutlinedIcon />,
          label: 'Add A Trainer',
          onClick: () => setIsAddTrainerModalOpened(true),
        },
      ]}
      searchPlaceholder={getSearchPlaceholder()}
      setSearchQuery={setSearchQuery}
      disableSearch={false}
    >
      {renderContent()}
      {renderAddProgramDialog()}
    </ContentDrill>
  );
};

export default Trainers;
